import {
  CheckCircleIcon,
  FormControl,
  IInputProps,
  Input,
  Text,
  View,
  WarningIcon,
} from 'native-base';
import React, {useEffect, useRef, useState} from 'react';

import {theme} from '../../theme/';

export interface FormFieldProps extends IInputProps {
  /**
   * Input id. Used in "for" attribute for label.
   * Required for accessibility and screen readers.
   */
  nativeID: string;
  /**
   * Value passed if the form field is valid
   */
  isValid?: boolean;
  /**
   * Label presented when the input is passed isInvalid prop (from native-base form groups)
   */
  isInvalidLabel?: string;
  /**
   * Label used for the input as well as a11y label
   */
  label?: string;
  /**
   * Overlay element to be put above the input box (must be styled and position absolute)
   * For example, on a password type box, a user might want to have a show/hide toggle-able.
   */
  overlayElement?: any;
  /**
   * Test ID to attach to the input element and relevant children elements.
   * Required as to to force developer to provide TestIDs on actionable items.
   */
  testID: string;
}

const FormField: React.FC<FormFieldProps> = ({label, onBlur, ...rest}) => {
  const {
    autoFocus,
    isInvalid,
    isValid,
    isInvalidLabel,
    value,
    nativeID,
    isDisabled,
    editable,
  } = rest;

  const [focused, setFocused] = useState<boolean>(autoFocus ?? false);

  const textInput = useRef<HTMLInputElement>();

  const handleBlurred = () => setFocused(false);
  const handleFocused = () => {
    if (!editable) {
      return;
    }
    setFocused(true);
    textInput?.current?.focus();
  };

  /**
   * Detect if chrome has auto-filled value on input if so set focused once on mount,
   * so label displays in right place.
   */
  useEffect(() => {
    setTimeout(() => {
      try {
        if (textInput?.current?.matches(':-internal-autofill-selected')) {
          setFocused(true);
        }
      } catch (e) {
        // ignore error
      }
    });
  }, []);

  return (
    <FormControl>
      <View flexDirection="row" alignItems="flex-end" width="100%">
        <Input
          width="100%"
          variant="unstyled"
          fontSize="16px"
          color={theme.colors.paleSky}
          opacity={isDisabled ? 0.4 : 1}
          ref={textInput}
          isInvalid={isInvalid}
          isValid={isValid}
          isDisabled={isDisabled}
          onFocus={handleFocused}
          onBlur={(e) => {
            handleBlurred();
            onBlur && onBlur(e);
          }}
          aria-label={`${label}`}
          backgroundColor="white"
          paddingTop="28px"
          paddingBottom="8px"
          paddingLeft="1px"
          borderRadius={0}
          borderBottomWidth={!editable ? null : focused ? '2px' : '1px'}
          borderBottomColor={
            isInvalid && editable && isInvalidLabel
              ? theme.colors.error
              : isValid && editable && !isInvalidLabel
              ? theme.colors.green
              : editable && focused
              ? theme.colors.black
              : !editable
              ? theme.colors.tuna
              : theme.colors.inputBorder
          }
          height="50px"
          numberOfLines={1}
          _focus={{borderWidth: 0}}
          overflowY="hidden"
          {...rest}
        />
        {isInvalid && isInvalidLabel && editable ? (
          <WarningIcon
            color={theme.colors.error}
            size="14px"
            mb="5px"
            ml="-15px"
          />
        ) : isValid && editable && !isInvalidLabel ? (
          <CheckCircleIcon
            color={theme.colors.valid}
            size="14px"
            mb="5px"
            ml="-15px"
          />
        ) : null}
      </View>
      {isInvalid && isInvalidLabel && (
        <FormControl.Label
          _text={{
            fontSize: '10px',
            color: theme.colors.error,
            lineHeight: '14px',
          }}
        >
          {isInvalidLabel}
        </FormControl.Label>
      )}
      {label && (
        <View
          paddingTop={!!value || focused ? '0' : '12px'}
          position="absolute"
        >
          <FormControl.Label htmlFor={nativeID}>
            <Text
              onPress={handleFocused}
              fontSize={!!value || focused ? '11px' : '14px'}
              fontFamily="body"
              fontWeight="bold"
              opacity={isDisabled ? 0.4 : 1}
              color={
                !!value || focused ? theme.colors.tuna : theme.colors.paleSky
              }
            >
              {label}
            </Text>
          </FormControl.Label>
        </View>
      )}
    </FormControl>
  );
};

export default FormField;
